import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, Typography, Button, Paper, AppBar, Toolbar, Avatar, 
  IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './Interview.module.css';
import Footer from './Footer';

function Interview() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [interviewId, setInterviewId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openResumeDialog, setOpenResumeDialog] = useState(false);
  const [isResumingInterview, setIsResumingInterview] = useState(false);
  const [existingInterviewId, setExistingInterviewId] = useState(null);
  const messagesEndRef = useRef(null);
  const textAreaRef = useRef(null);
  const recognition = useRef(null);
  const navigate = useNavigate();

  const email = localStorage.getItem('email');
  const firstName = localStorage.getItem('firstName');
  const lastName = localStorage.getItem('lastName');

  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = true;
      recognition.current.interimResults = true;

      recognition.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInput(prevInput => prevInput + transcript);
      };

      recognition.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  const startInterview = useCallback(async () => {
    try {
      console.log('Starting or resuming interview...');
      const storedInterviewId = localStorage.getItem('interviewId');
      
      if (storedInterviewId && !isResumingInterview) {
        setExistingInterviewId(storedInterviewId);
        setOpenResumeDialog(true);
        return;
      }

      let response;
      if (isResumingInterview) {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/resume-interview`, {
          interview_id: existingInterviewId,
          user_id: email
        });
      } else {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/start-interview`, {
          user_id: email,
          first_name: firstName,
          last_name: lastName
        });
      }

      console.log('Interview started/resumed:', response.data);
      setInterviewId(response.data.interview_id);
      localStorage.setItem('interviewId', response.data.interview_id);

      setMessages(response.data.messages.map(msg => ({
        text: msg.content,
        sender: msg.role === 'assistant' ? 'bot' : 'user',
        senderName: msg.role === 'assistant' ? 'Bot' : `${firstName} ${lastName}`
      })));
    } catch (error) {
      console.error('Error starting/resuming interview:', error);
      alert('There was an error starting the interview. Please try again.');
    }
  }, [email, firstName, lastName, isResumingInterview, existingInterviewId]);

  useEffect(() => {
    if (!email) {
      console.log('No email found, redirecting to login');
      navigate('/');
    } else {
      startInterview();
    }
  }, [email, navigate, startInterview]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
    adjustTextAreaHeight();
  };

  const adjustTextAreaHeight = () => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto';
      textAreaRef.current.style.height = textAreaRef.current.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    adjustTextAreaHeight();
  }, [input]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (input.trim() === '' || !interviewId) return;

    setMessages([...messages, { text: input, sender: 'user', senderName: `${firstName} ${lastName}` }]);
    setInput('');
    setIsTyping(true);

    try {
      console.log('Sending message:', input);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/chat`, { 
        interview_id: interviewId,
        message: input 
      });
      console.log('Received response:', response.data);
      setIsTyping(false);
      setMessages(prevMessages => [...prevMessages, { text: response.data.response, sender: 'bot', senderName: 'Bot' }]);
    } catch (error) {
      console.error('Error:', error);
      setIsTyping(false);
      setMessages(prevMessages => [...prevMessages, { text: "Sorry, there was an error processing your request.", sender: 'bot', senderName: 'Bot' }]);
    }
  };

  const handleVoiceInput = () => {
    if (!recognition.current) {
      alert("Sorry, your browser doesn't support speech recognition.");
      return;
    }

    if (isListening) {
      recognition.current.stop();
      setIsListening(false);
    } else {
      recognition.current.start();
      setIsListening(true);
    }
  };

  const handleFinish = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = async () => {
    setOpenDialog(false);
    try {
      console.log('Completing interview...');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/complete-interview/${interviewId}`);
      console.log('Interview completed successfully');
      
      localStorage.clear();
      console.log('Local storage cleared');

      console.log('Navigating to login page...');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error completing interview:', error);
      alert('There was an error completing the interview. Please try again.');
    }
  };

  const handleResumeConfirm = () => {
    setIsResumingInterview(true);
    setOpenResumeDialog(false);
    startInterview();
  };

  const handleResumeCancel = () => {
    setOpenResumeDialog(false);
    localStorage.removeItem('interviewId');
    startInterview();
  };

  const getInitials = (name) => {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase();
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <Box className="container">
      <AppBar position="fixed" className="mainAppBar">
        <Toolbar>
          <img src="/aqiliSmartObject.png" alt="Logo" style={{ height: 40, marginRight: 16 }} />
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            aqili
          </Typography>
          <Button color="inherit" onClick={handleFinish}>Finish Interview</Button>
        </Toolbar>
      </AppBar>
      
      <Box className={styles.mainContent}>
        <Paper className={styles.chatContainer} elevation={3}>
          <AnimatePresence>
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className={`${styles.messageBox} ${message.sender === 'user' ? styles.userMessage : styles.botMessage}`}
              >
                {message.sender !== 'user' && (
                  <Avatar className={styles.avatar}>
                    {getInitials(message.senderName)}
                  </Avatar>
                )}
                <Paper className={`${styles.messageBubble} ${message.sender === 'user' ? styles.userBubble : styles.botBubble}`}>
                  <Typography variant="body1">{message.text}</Typography>
                </Paper>
                {message.sender === 'user' && (
                  <Avatar className={styles.avatar}>
                    {getInitials(message.senderName)}
                  </Avatar>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
          {isTyping && (
            <Box className={`${styles.messageBox} ${styles.botMessage}`}>
              <Avatar className={styles.avatar}>
                {getInitials('Bot')}
              </Avatar>
              <Paper className={`${styles.messageBubble} ${styles.botBubble} ${styles.typingIndicator}`}>
                <Typography variant="body2">AI is typing<span className={styles.typingAnimation}>...</span></Typography>
              </Paper>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Paper>
      </Box>

      <Box className={styles.inputContainer}>
        <textarea
          ref={textAreaRef}
          value={input}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
          className={styles.textArea}
          rows={1}
        />
        <IconButton
          onClick={handleVoiceInput}
          className={`${styles.voiceButton} ${isListening ? styles.voiceActive : ''}`}
        >
          <MicIcon />
        </IconButton>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          endIcon={<SendIcon />}
          className={styles.sendButton}
        >
          Send
        </Button>
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Finish Interview"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to finish the interview? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDialogClose} color="primary" autoFocus>
            Finish Interview
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openResumeDialog}
        onClose={handleResumeCancel}
        aria-labelledby="resume-dialog-title"
        aria-describedby="resume-dialog-description"
      >
        <DialogTitle id="resume-dialog-title">{"Resume Interview"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="resume-dialog-description">
            You have an unfinished interview. Would you like to resume?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResumeCancel} color="primary">
            Start New
          </Button>
          <Button onClick={handleResumeConfirm} color="primary" autoFocus>
            Resume
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </Box>
  );
}

export default Interview;